import agwaPageHeaderStyles from './agwa-page-header.styles.css?inline';
import { LitElement, html, nothing, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/badge/badge.js';
import { router } from '../router';

/**
 * One step of the trail above a page: the name of a place and the route that opens it. The
 * root of the trail comes first. Every crumb is a real destination - a list or an entity page -
 * never a heading with nowhere to go, because a crumb that cannot be followed is a label
 * pretending to be a link.
 */
export interface PageCrumb {
  label: string;
  href: string;
}

/**
 * `agwa-page-header` - a page's title, the trail of places that contain it, its badges and its
 * actions.
 *
 * THE TRAIL IS THE STANDARD (architect, 2026-09-06, superseding the arrow parent-link of
 * 2026-08-10 while keeping its principle). The 2026-08-10 ruling was that a control which goes
 * to one place is a link NAMED for that place and is never called "Back". That still holds; the
 * trail is the same idea carried to its conclusion. A page rarely sits inside one container: a
 * report sits inside a product's reports, which sit inside the product, which sits inside the
 * register. One arrow could name only one of those, so the reader was told the nearest list and
 * left to guess the rest. The trail names every container, root first, each one a real anchor.
 *
 * Shape of the trail: `{root list} > {owning entity} > ... > {this page}`. The leaf is the
 * slotted title, not a link - it is where the reader already is. State and classification chips
 * sit on the same line right after the leaf (the `badges` slot), and the page's actions ride the
 * right of the row (the `actions` slot). The trail is constant across a page's tabs: a tab is a
 * view of the leaf, not a place below it.
 *
 * Still a real anchor per crumb, so modified clicks are left to the browser and plain clicks are
 * routed - the same contract `agwa-row-actions` settled on, for the same reasons: middle-click,
 * cmd-click and "open in new tab" work, and the destination shows in the status bar before the
 * reader commits to it. It imports the router directly, as `header.ts` and `card-menu-item.ts`
 * do: this is page chrome whose whole job is navigation, not a content component that must
 * survive being dropped into an arbitrary host.
 *
 * `parentHref` / `parentLabel` remain for a page that has not yet declared its trail: they render
 * the single arrow link of the 2026-08-10 shape. When `crumbs` is set the arrow is suppressed -
 * the trail already names the parent, and naming it twice would be noise.
 */
@customElement('agwa-page-header')
export class AgwaPageHeader extends LitElement {
  /**
   * Compact density: smaller title, tighter gaps. The report pages adopted it
   * when the architect called their chrome "just too heavy" (2026-09-05) -
   * the actions slot plus compact collapses what was a title row AND a framed
   * action-bar row into one quiet line. Opt-in so no other page shifts.
   */
  @property({ type: Boolean, reflect: true }) compact = false;

  /**
   * The places that contain this page, root first. Empty renders no trail, which is correct for
   * a top-level list - it has nothing above it to name.
   */
  @property({ attribute: false }) crumbs: PageCrumb[] = [];

  /**
   * Where the single arrow link goes, for a page that has not declared a trail. Ignored once
   * `crumbs` is set. Empty renders no link at all.
   */
  @property({ type: String }) parentHref: string = '';

  /**
   * The name of the arrow link's destination, shown on screen. It is the NAME OF A PLACE
   * ("Certificates", "Products"), never a direction: a direction is what made the old control
   * unreadable.
   */
  @property({ type: String }) parentLabel: string = '';

  /**
   * Whether anything is slotted as a badge. The trail layout seats badges on the trail line after
   * the leaf, and an empty item would still take its gap - so the item hides while the slot holds
   * nothing. Tracked from `slotchange` because a slot's content is the host's, not this
   * component's, and can arrive after first render (badges appear once a record loads).
   */
  @state() private hasBadges = false;

  static styles = unsafeCSS(agwaPageHeaderStyles);

  /**
   * Route a plain primary click; leave every other kind to the browser. Modified clicks and
   * non-primary buttons are how "open in new tab" works on what is, after all, a link.
   */
  private routeClick(e: MouseEvent, href: string) {
    if (
      e.defaultPrevented ||
      e.button !== 0 ||
      e.metaKey ||
      e.ctrlKey ||
      e.shiftKey ||
      e.altKey
    ) {
      return;
    }
    e.preventDefault();
    // `void` because the route change IS the outcome - there is no header state to roll back if the
    // navigation rejects.
    void router?.navigate(href);
  }

  private onParentClick(e: MouseEvent) {
    this.routeClick(e, this.parentHref);
  }

  private onBadgesSlotChange(e: Event) {
    const slot = e.target as HTMLSlotElement;
    this.hasBadges = slot.assignedElements({ flatten: true }).length > 0;
  }

  /** The trail: every container root first, each a link, then the leaf title in place. */
  private renderTrail() {
    return html`
      <nav class="trail" aria-label="Breadcrumb">
        <ol class="trail-list">
          ${this.crumbs.map(
            (crumb) => html`
              <li class="trail-item">
                <a
                  class="crumb-link"
                  href=${crumb.href}
                  @click=${(e: MouseEvent) => this.routeClick(e, crumb.href)}
                  >${crumb.label}</a
                >
                <sl-icon
                  name="chevron-right"
                  class="crumb-sep"
                  aria-hidden="true"
                ></sl-icon>
              </li>
            `
          )}
          <li class="trail-item trail-leaf" aria-current="page">
            <h1 class="title-content"><slot></slot></h1>
          </li>
          <!-- Badges ride the same line as the leaf, right after it (architect, 2026-09-06:
               "badge should not be on a second line"). The item hides while nothing is slotted
               so a page without badges has no phantom gap after its title. -->
          <li
            class="trail-item trail-badges ${this.hasBadges ? '' : 'badges-empty'}"
          >
            <slot name="badges" @slotchange=${this.onBadgesSlotChange}></slot>
          </li>
        </ol>
      </nav>
    `;
  }

  render() {
    const hasTrail = this.crumbs.length > 0;
    return html`
      <div class="header-row ${hasTrail ? 'trail-mode' : ''}">
        ${
          !hasTrail && this.parentHref && this.parentLabel
            ? html`<div class="parent-col">
                <a
                  class="parent-link"
                  href=${this.parentHref}
                  @click=${this.onParentClick}
                >
                  <sl-icon name="arrow-left"></sl-icon>
                  <span class="parent-label">${this.parentLabel}</span>
                </a>
              </div>`
            : nothing
        }
        <div class="title-col">
          ${
            hasTrail
              ? this.renderTrail()
              : html`<h1 class="title-content"><slot></slot></h1>
                  <div class="badges-slot-wrapper">
                    <slot name="badges"></slot>
                  </div>`
          }
        </div>
        <div class="actions-col">
          <slot name="actions"></slot>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'agwa-page-header': AgwaPageHeader;
  }
}
